import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'

import { useTranslate } from '@tolgee/vue'

import { useRealOnline } from './useRealOnline'

const ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module', // This error arises when the application cannot retrieve a module due to asset changes introduced in a new release.
  'Importing a module script failed' // This error can occur from changes in dependencies that the new release doesn't resolve.
]

export function useAppUpdateNotice() {
  const online = useRealOnline()
  const router = useRouter()
  const { t } = useTranslate()

  const refreshTo = ref<string | undefined>()

  router.onError((error, to) => {
    if (ERROR_PATTERNS.some((errorPattern) => error.message.includes(errorPattern))) {
      refreshTo.value = to.fullPath
    }
  })

  function confirm() {
    if (refreshTo.value) {
      ;(window as Window).location = refreshTo.value
      refreshTo.value = undefined
    }
  }

  const kind = computed<'update' | 'offline' | null>(() => {
    if (!online.value) return 'offline'
    if (refreshTo.value) return 'update'
    return null
  })

  const translations = computed(() => {
    if (kind.value === 'offline') {
      return {
        title: t.value({ key: 'app.offline.title', defaultValue: 'You are offline' }),
        message: t.value({
          key: 'app.offline.message',
          defaultValue: 'Please check your internet connection and try again.'
        })
      }
    }
    return {
      title: t.value({ key: 'app.refresh.title', defaultValue: 'Application Update Notice' }),
      message: t.value({
        key: 'app.refresh.message',
        defaultValue:
          'It seems like a new version of this application has been released, which may have caused an issue while loading resources. We suggest to reload the page to ensure you have the latest updates.'
      })
    }
  })

  return {
    open: computed(() => kind.value !== null),
    kind,
    confirm,
    translations
  }
}
